import React, { useState, useEffect } from 'react';
import { FileText, Phone, User, Calendar, CheckCircle, Clock, XCircle } from 'lucide-react';
import Button from '../components/UI/Button';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

interface ValuationRequest {
  id: string;
  name: string;
  phone: string;
  asset_type: string;
  description: string;
  status: 'pending' | 'contacted' | 'completed' | 'rejected';
  created_at: string;
}

const AdminValuationRequestsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<ValuationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchRequests();
  }, [user, navigate, filter]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://tbiz.ir'}/backend/api.php?path=valuation-requests&status=${filter}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('tbiz_auth_token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setRequests(data.requests || []);
      }
    } catch (error) {
      console.error('Error fetching valuation requests:', error);
    }
    setLoading(false);
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://tbiz.ir'}/backend/api.php?path=valuation-requests`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('tbiz_auth_token')}`,
        },
        body: JSON.stringify({ id, status }),
      });
      
      const data = await response.json();
      if (data.success) {
        fetchRequests();
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getAssetTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      'brand': 'برند و علامت تجاری',
      'patent': 'اختراع و دانش فنی',
      'business': 'کسب‌وکارهای آماده',
      'software': 'نرم‌افزار و اپلیکیشن',
    };
    return types[type] || type;
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { label: string; className: string; icon: any }> = {
      'pending': { label: 'در انتظار', className: 'bg-yellow-100 text-yellow-700', icon: Clock },
      'contacted': { label: 'تماس گرفته شده', className: 'bg-blue-100 text-blue-700', icon: Phone },
      'completed': { label: 'تکمیل شده', className: 'bg-green-100 text-green-700', icon: CheckCircle },
      'rejected': { label: 'رد شده', className: 'bg-red-100 text-red-700', icon: XCircle },
    };
    const badge = badges[status] || badges['pending'];
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${badge.className}`}>
        <Icon size={14} />
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">در حال بارگذاری...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">درخواست‌های ارزش‌گذاری</h1>
          <p className="text-gray-600">مدیریت درخواست‌های ارزش‌گذاری کاربران</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex gap-2 flex-wrap">
            {['all', 'pending', 'contacted', 'completed', 'rejected'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  filter === status
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status === 'all' ? 'همه' : getStatusBadge(status).props.children[1]}
              </button>
            ))}
          </div>
        </div>

        {/* Requests List */}
        {requests.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <FileText size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">هیچ درخواستی یافت نشد</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {requests.map((request) => (
              <div key={request.id} className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <User size={20} className="text-gray-400" />
                      <h3 className="text-lg font-bold text-gray-900">{request.name}</h3>
                      {getStatusBadge(request.status)}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                      <div className="flex items-center gap-1">
                        <Phone size={16} />
                        <a href={`tel:${request.phone}`} className="hover:text-teal-600">{request.phone}</a>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar size={16} />
                        {new Date(request.created_at).toLocaleDateString('fa-IR')}
                      </div>
                    </div>
                    <div className="mb-3">
                      <span className="text-xs font-medium text-gray-500">نوع دارایی:</span>
                      <span className="text-sm font-bold text-gray-700 mr-2">{getAssetTypeLabel(request.asset_type)}</span>
                    </div>
                    {request.description && (
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-700">{request.description}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-4 border-t border-gray-100">
                  {request.status === 'pending' && (
                    <>
                      <Button
                        onClick={() => updateStatus(request.id, 'contacted')}
                        className="bg-blue-600 hover:bg-blue-700 text-sm"
                      >
                        تماس گرفته شد
                      </Button>
                      <Button
                        onClick={() => updateStatus(request.id, 'rejected')}
                        variant="ghost"
                        className="text-red-600 hover:bg-red-50 text-sm"
                      >
                        رد کردن
                      </Button>
                    </>
                  )}
                  {request.status === 'contacted' && (
                    <Button
                      onClick={() => updateStatus(request.id, 'completed')}
                      className="bg-green-600 hover:bg-green-700 text-sm"
                    >
                      تکمیل شد
                    </Button>
                  )}
                  {(request.status === 'completed' || request.status === 'rejected') && (
                    <Button
                      onClick={() => updateStatus(request.id, 'pending')}
                      variant="ghost"
                      className="text-gray-600 hover:bg-gray-100 text-sm"
                    >
                      بازگشت به در انتظار
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminValuationRequestsPage;
